import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// PrimeNG Imports
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarModule } from 'primeng/avatar';
import { TabsModule } from 'primeng/tabs';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { ProgressBarModule } from 'primeng/progressbar';
import { BadgeModule } from 'primeng/badge';
import { TableModule } from 'primeng/table';
import { PanelModule } from 'primeng/panel';
import { TooltipModule } from 'primeng/tooltip';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ConfigService } from 'src/app/_services/config.service';
import { PortalService } from '../_services/portal.service';
import { Router } from '@angular/router';
import { User } from 'src/app/_models/User.model';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Role } from 'src/app/_models/portal/Role.model';
import { Portal } from 'src/app/_models/portal/Portal.model';

@Component({
	selector: 'app-user-profile',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		CardModule,
		ButtonModule,
		InputTextModule,
		AvatarModule,
		TabsModule,
		DividerModule,
		TagModule,
		ChipModule,
		ToggleButtonModule,
		DatePickerModule,
		SelectModule,
		ProgressBarModule,
		BadgeModule,
		TableModule,
		PanelModule,
		TooltipModule,
		SelectButtonModule,
		ToastModule,
		TranslatePipe,
	],
	providers: [MessageService],
	templateUrl: './profilo.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./profilo.component.css'],
})
export class ProfiloComponent implements OnInit {
	constructor(
		private router: Router,
		public configService: ConfigService,
		private portalservice: PortalService,
		private translate: TranslateService,
		private messageService: MessageService,
	) {}

	user!: User;

	isEditMode = false;
	savingProfile = false;
	editableUser: User = { ...this.user };

	// Verifica del numero: il telefono diventa identita' di accesso solo dopo l'OTP
	phoneGuid = '';
	phoneOtp = '';
	phoneTokenSent = false;
	phoneVerified = false;
	phoneSending = false;
	phoneChecking = false;

	avatarUploading = false;

	assignedPortals!: Portal[];
	assignedRoles!: Role[];

	// Preferenze notifica (Email / Notifica portale / Niente) per evento
	mailSettings: any[] = [];
	mailSettingsByCategory: { category: string; items: any[] }[] = [];
	savingMailSettings = false;
	mailSearch = '';
	private mailSettingsSnapshot = '';
	channelOptions = [
		{ label: 'profile.notify.channel.email', value: 'email', icon: 'pi pi-envelope' },
		{ label: 'profile.notify.channel.portal', value: 'portal', icon: 'pi pi-bell' },
		{ label: 'profile.notify.channel.none', value: 'none', icon: 'pi pi-ban' },
	];
	// Icona per categoria di notifica (fallback: campanella)
	private categoryIcons: { [k: string]: string } = {
		health: 'pi pi-heart',
		training: 'pi pi-book',
		safety: 'pi pi-shield',
		documents: 'pi pi-file',
		contract: 'pi pi-briefcase',
		system: 'pi pi-megaphone',
		security: 'pi pi-lock',
		rfi_ticket: 'pi pi-ticket',
		rfi_dispositivi: 'pi pi-mobile',
		rfi_sopralluogo: 'pi pi-map-marker',
		rfi_certificati: 'pi pi-verified',
	};

	ngOnInit() {
		this.getUser();
		this.getUserRoles();
		this.getUserPortals();
		this.loadMailSettings();
	}

	loadMailSettings() {
		this.portalservice.getMyMailSettings(this.configService.getCompany_id()).subscribe({
			next: (items: any[]) => {
				this.mailSettings = items || [];
				this.groupMailSettings();
				this.mailSettingsSnapshot = this.snapshotMailSettings();
			},
		});
	}

	private groupMailSettings() {
		const groups: { [k: string]: any[] } = {};
		for (const it of this.mailSettings) {
			const cat = it.umst_category || 'altro';
			(groups[cat] = groups[cat] || []).push(it);
		}
		this.mailSettingsByCategory = Object.keys(groups).map((category) => ({ category, items: groups[category] }));
	}

	private snapshotMailSettings(): string {
		return JSON.stringify(this.mailSettings.map((it) => `${it.umst_id}:${it.effective_channel}`));
	}

	// True se l'utente ha modificato almeno una preferenza rispetto allo stato caricato/salvato
	get mailSettingsDirty(): boolean {
		return this.snapshotMailSettings() !== this.mailSettingsSnapshot;
	}

	// Categorie/notifiche filtrate dalla ricerca testuale (per descrizione o nome categoria)
	get filteredMailSettingsByCategory(): { category: string; items: any[] }[] {
		const q = this.mailSearch.trim().toLowerCase();
		if (!q) {
			return this.mailSettingsByCategory;
		}
		return this.mailSettingsByCategory
			.map((group) => {
				const catMatch = this.categoryLabel(group.category).toLowerCase().includes(q);
				const items = catMatch ? group.items : group.items.filter((it) => (it.umst_desc || '').toLowerCase().includes(q));
				return { category: group.category, items };
			})
			.filter((group) => group.items.length > 0);
	}

	clearMailSearch() {
		this.mailSearch = '';
	}

	categoryLabel(cat: string): string {
		const key = 'profile.notify.category.' + cat;
		const label = this.translate.instant(key);
		// Fallback: se la chiave non è tradotta, mostra il nome leggibile (es. "Rfi Ticket")
		if (!label || label === key) {
			return cat.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
		}
		return label;
	}

	categoryIcon(cat: string): string {
		return this.categoryIcons[cat] || 'pi pi-bell';
	}

	channelIcon(channel: string): string {
		switch (channel) {
			case 'email':
				return 'pi pi-envelope';
			case 'portal':
				return 'pi pi-bell';
			default:
				return 'pi pi-ban';
		}
	}

	// Imposta lo stesso canale per tutte le notifiche di una categoria
	setCategoryChannel(group: { category: string; items: any[] }, channel: string) {
		for (const item of group.items) {
			item.effective_channel = channel;
		}
	}

	saveMailSettings() {
		this.savingMailSettings = true;
		const payload = this.mailSettings.map((it) => ({ umst_id: it.umst_id, ums_channel: it.effective_channel }));
		this.portalservice.saveMyMailSettings(payload).subscribe({
			next: () => {
				this.savingMailSettings = false;
				this.mailSettingsSnapshot = this.snapshotMailSettings();
				this.messageService.add({
					severity: 'success',
					summary: this.translate.instant('toast.successo'),
					detail: this.translate.instant('profile.notify.saved'),
				});
			},
			error: () => {
				this.savingMailSettings = false;
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('toast.errore'),
					detail: this.translate.instant('profile.notify.save_error'),
				});
			},
		});
	}

	goToAccountDeletion() {
		this.router.navigate(['/eliminazione-account']);
	}

	// ── Immagine profilo ────────────────────────────────────────
	onAvatarSelected(event: Event) {
		const input = event.target as HTMLInputElement;
		if (!input.files || input.files.length === 0) return;
		const file = input.files[0];
		input.value = ''; // consente di riselezionare lo stesso file
		this.resizeSquare(file)
			.then((dataUrl) => this.saveAvatar(dataUrl))
			.catch(() => {
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('toast.errore'),
					detail: "Impossibile leggere l'immagine selezionata",
				});
			});
	}

	removeAvatar() {
		this.saveAvatar('');
	}

	private saveAvatar(dataUrl: string) {
		this.avatarUploading = true;
		this.portalservice.updateAvatar(dataUrl).subscribe({
			next: () => {
				this.avatarUploading = false;
				this.user.avatar = dataUrl;
				this.configService.setUserAvatar(dataUrl);
				this.messageService.add({
					severity: 'success',
					summary: this.translate.instant('toast.successo'),
					detail: dataUrl ? 'Immagine profilo aggiornata' : 'Immagine profilo rimossa',
				});
			},
			error: () => {
				this.avatarUploading = false;
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('toast.errore'),
					detail: "Errore durante il salvataggio dell'immagine profilo",
				});
			},
		});
	}

	// Ridimensiona e ritaglia l'immagine in un quadrato 256x256 JPEG, restituendo un data-URL base64.
	private resizeSquare(file: File): Promise<string> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			const url = URL.createObjectURL(file);
			img.onload = () => {
				const size = 256;
				const side = Math.min(img.width, img.height);
				const sx = (img.width - side) / 2;
				const sy = (img.height - side) / 2;
				const canvas = document.createElement('canvas');
				canvas.width = size;
				canvas.height = size;
				const ctx = canvas.getContext('2d')!;
				// Sfondo bianco: il JPEG non ha canale alpha, quindi le zone trasparenti
				// di un PNG verrebbero composte su nero senza questo riempimento.
				ctx.fillStyle = '#ffffff';
				ctx.fillRect(0, 0, size, size);
				ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
				URL.revokeObjectURL(url);
				resolve(canvas.toDataURL('image/jpeg', 0.85));
			};
			img.onerror = (e) => {
				URL.revokeObjectURL(url);
				reject(e);
			};
			img.src = url;
		});
	}

	getUser() {
		this.portalservice.getUser(this.configService.getUserID()).subscribe({
			next: (user) => {
				// Solo i data-URL sono avatar validi: il DB legacy conteneva path tipo
				// '/avatar/default.svg' (file inesistente) → immagine rotta
				user.avatar = (user.avatar || '').startsWith('data:image') ? user.avatar : '';
				this.user = user;
				this.editableUser = { ...this.user };
				// Allinea l'avatar nel currentUser locale (usato da header) al valore dal backend
				if (user.avatar !== this.configService.getUserAvatar()) {
					this.configService.setUserAvatar(user.avatar);
				}
			},
		});
	}

	getUserRoles() {
		this.portalservice.getRuoliUser(this.configService.getUserID()).subscribe({
			next: (roles) => {
				this.assignedRoles = roles?.filter((role: Role) => role.rl_isChecked);
			},
		});
	}

	getUserPortals() {
		this.portalservice.getPortali('', 'pt_descrizione', '', 1, 9999).subscribe({
			next: (portale: Portal[]) => {
				this.assignedPortals = portale;
			},
		});
	}

	toggleEditMode() {
		if (!this.isEditMode) {
			this.editableUser = { ...this.user };
			this.isEditMode = true;
			return;
		}

		// Si salva la sola anagrafica: mail e telefono sono identita' di accesso e si
		// modificano dai rispettivi flussi con verifica, non da qui
		this.savingProfile = true;
		this.portalservice.upd_user_nominativo(this.editableUser.usr_firstname, this.editableUser.usr_lastname, this.editableUser.usr_displayname).subscribe({
			next: () => {
				this.savingProfile = false;
				this.user = { ...this.editableUser };
				this.isEditMode = false;
				// Allinea il nome mostrato nell'header, che legge da currentUser
				const currentUser = this.configService.getUser();
				if (currentUser) {
					currentUser.usr_displayname = this.editableUser.usr_displayname;
					localStorage.setItem('currentUser', JSON.stringify(currentUser));
				}
				this.messageService.add({ severity: 'success', summary: 'Profilo aggiornato', detail: 'I tuoi dati sono stati salvati' });
			},
			error: (err) => {
				this.savingProfile = false;
				this.messageService.add({ severity: 'error', summary: 'Salvataggio non riuscito', detail: this.businessError(err) });
			},
		});
	}

	cancelEdit() {
		this.editableUser = { ...this.user };
		this.isEditMode = false;
	}

	/** Manda l'OTP al numero indicato: diventa identita' di accesso solo dopo la conferma. */
	sendPhoneToken() {
		const phone = this.normalizedPhone();
		if (!/^\+[0-9]{8,15}$/.test(phone)) {
			this.messageService.add({ severity: 'warn', summary: 'Numero non valido', detail: 'Indica il numero con il prefisso internazionale, es. +39...' });
			return;
		}

		this.phoneSending = true;
		this.phoneGuid = crypto.randomUUID();
		this.portalservice.send_token_phone(phone, this.phoneGuid).subscribe({
			next: () => {
				this.phoneSending = false;
				this.phoneTokenSent = true;
				this.phoneOtp = '';
				this.messageService.add({ severity: 'success', summary: 'Codice inviato', detail: 'Ti abbiamo mandato un SMS con il codice di conferma' });
			},
			error: (err) => {
				this.phoneSending = false;
				this.messageService.add({ severity: 'error', summary: 'Invio non riuscito', detail: this.businessError(err) });
			},
		});
	}

	checkPhoneToken() {
		if (!this.phoneOtp || this.phoneOtp.length !== 6) {
			this.messageService.add({ severity: 'warn', summary: 'Codice incompleto', detail: 'Il codice di conferma e\' di 6 cifre' });
			return;
		}

		const phone = this.normalizedPhone();
		this.phoneChecking = true;
		this.portalservice.check_token_phone(phone, this.phoneOtp, this.phoneGuid).subscribe({
			next: () => {
				this.phoneChecking = false;
				this.phoneVerified = true;
				this.phoneTokenSent = false;
				this.user.usr_phone = phone;
				this.editableUser.usr_phone = phone;
				this.messageService.add({ severity: 'success', summary: 'Numero verificato', detail: 'Da ora puoi accedere anche con il tuo numero di telefono' });
			},
			error: (err) => {
				this.phoneChecking = false;
				this.messageService.add({ severity: 'error', summary: 'Verifica non riuscita', detail: this.businessError(err) });
			},
		});
	}

	cancelPhoneVerification() {
		this.phoneTokenSent = false;
		this.phoneOtp = '';
	}

	private normalizedPhone(): string {
		return (this.editableUser.usr_phone ?? '').toString().replace(/[\s\-()]/g, '');
	}

	/** Il backend manda gli errori di business come stringa "KCS|messaggio". */
	private businessError(err: any): string {
		const raw = typeof err?.error === 'string' ? err.error : '';
		return raw.startsWith('KCS|') ? raw.replace('KCS|', '') : 'Errore imprevisto, riprova';
	}

	getRoleSeverity(roleType: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
		switch (roleType.toLowerCase()) {
			case 'admin':
				return 'danger';
			case 'developer':
				return 'info';
			case 'manager':
				return 'warn';
			case 'user':
				return 'success';
			default:
				return 'secondary';
		}
	}

	getPortalStatusSeverity(isActive: boolean): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' | undefined {
		return isActive ? 'success' : 'secondary';
	}

	getActivePortalsCount(): number {
		// Chiamato dai binding di template prima che la lista arrivi dal backend: senza
		// guard rilancia a ogni ciclo di change detection
		return this.assignedPortals?.length || 0;
	}

	getActiveRolesCount(): number {
		return this.assignedRoles?.filter((role) => role.rl_isChecked).length || 0;
	}
}
